import { Sizes } from "@JohnLewisPartnership/jl-design-system/dist/components/size/SizeList";
import Size from "@JohnLewisPartnership/jl-design-system/dist/components/size/Size";
import { Dispatch, useState } from "react";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";
import { getVariantDimensions } from "@/utils/product/getVariantDimensions";
import styles from "./GiftWithPurchaseSizes.module.scss";
import { Product, Variant, VariantGroup } from "../../../../../types/types";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { PlusOutlined as Plus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlusOutlined";
import { MinusOutlined as Minus24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/MinusOutlined";

type sizeType = {
  name: string;
  sublabel?: string;
  unavailable: boolean;
  isSelected: boolean;
  sizeHeadline: string;
};

type GiftWithPurchaseSizesProps = {
  product: Product;
  selectedVariant?: Variant;
  setSelectedVariant: Dispatch<Variant | undefined>;
  selectedVariantGroup?: VariantGroup;
  isMobile: boolean;
};

export const useAvailableSizes = (
  product: Product,
  selectedVariantGroup?: VariantGroup,
  selectedVariant?: Variant,
) => {
  const productVariants: Variant[] = product.variants;
  const initialVal: sizeType[] = [];
  const sizesList = productVariants.reduce((result, variant) => {
    const isIncludedSize = selectedVariantGroup?.value
      ? variant.differentiators?.size &&
        variant.differentiators?.colour === selectedVariantGroup?.value
      : variant.differentiators?.size;

    if (isIncludedSize) {
      const isUnavailable =
        (!variant.availability?.availableToOrder ||
          variant.availability?.shipFromStore) &&
        !isInStoreOnly(variant);

      result.push({
        name: variant?.differentiators?.size || "",
        sublabel: getVariantDimensions(variant),
        unavailable: isUnavailable,
        isSelected:
          selectedVariant?.differentiators?.size ===
          variant.differentiators?.size,
        sizeHeadline: variant?.differentiators?.sizeHeadline || "",
      });
    }
    return result;
  }, initialVal);
  return sizesList;
};

const sortSizeOptions = (sizes: sizeType[]) => {
  const sizeOptions = [...sizes];

  const sizeOptionsAvailable = sizeOptions.filter(
    (sizeOption) => !sizeOption.unavailable,
  );

  const sizeOptionsNotAvailable = sizeOptions.filter(
    (sizeOption) => sizeOption.unavailable,
  );

  const combinedSizeOptions = [
    ...sizeOptionsAvailable,
    ...sizeOptionsNotAvailable,
  ];

  return combinedSizeOptions;
};

export const GiftWithPurchaseSizes: React.FC<GiftWithPurchaseSizesProps> = (
  giftWithPurchaseSizesProps: GiftWithPurchaseSizesProps,
) => {
  const {
    product,
    selectedVariant,
    setSelectedVariant,
    selectedVariantGroup,
    isMobile,
  } = giftWithPurchaseSizesProps;

  const [sizeListExpanded, setSizeListExpanded] = useState(false);

  const chooseSelectedSize = (size: string) => {
    const chooseSizeVariant: Variant[] = product?.variants?.filter(
      (variant: Variant) => {
        if (selectedVariantGroup) {
          return (
            variant.differentiators?.size === size &&
            variant.differentiators?.colour === selectedVariantGroup?.value
          );
        } else {
          return variant.differentiators?.size === size;
        }
      },
    );
    setSelectedVariant(chooseSizeVariant[0]);
  };

  const sizesList = useAvailableSizes(
    product,
    selectedVariantGroup,
    selectedVariant,
  );

  const sortedSizeOptions = sortSizeOptions(sizesList);

  const displayedSizeList = sizeListExpanded
    ? sortedSizeOptions
    : isMobile
      ? sortedSizeOptions.slice(0, 2)
      : sortedSizeOptions.slice(0, 3);

  return (
    <>
      <Sizes
        selectedLabel={
          displayedSizeList.filter((size) => size.isSelected)[0]?.name
        }
        sublabel={
          displayedSizeList.filter((size) => size.isSelected)[0]?.sublabel
        }
        onClick={(size: string) => {
          chooseSelectedSize(size);
        }}
        className={styles.sizesContainer}
        brand={product.brand}
        hasDiorPromosEnabled={true}
      >
        {displayedSizeList.map((size, index) => (
          <Size
            key={`size-${index}`}
            id={`size-${size.name}`}
            data-testid="size:option"
            unavailable={size.unavailable}
            value={size.name}
            selected={size.isSelected}
            minimiseLabelPadding={true}
            className={styles.size}
          >
            {size.name}
          </Size>
        ))}
      </Sizes>
      {sizesList.length > 3 && (
        <TertiaryButton
          onClick={() => {
            setSizeListExpanded(!sizeListExpanded);
          }}
          IconComponent={
            sizeListExpanded ? Minus24PxOutlinedIcon : Plus24PxOutlinedIcon
          }
          iconPositionRight
          data-testid="gift-with-purchase-expand-sizes"
          small
          className={styles.showMoreButton}
        >
          {sizeListExpanded
            ? "Show less"
            : `Show ${sizesList.length - displayedSizeList.length} more`}
        </TertiaryButton>
      )}
    </>
  );
};
